import { Role, ROLE_SET_TYPE, ROLE_TYPE, SPECIAL_ROLE_ID } from '../../../typings/proto';
import { COMMON_CHANNEL_ID } from '../../../constant/channel';
import roleApi from '../../api/role';
import EventBus from '../../../util/event-bus';
import { getGroupChannelKey } from '../util';

export enum RoleManagerUpdateType {
  updateNormal,
  // 一定从远程获取，并且写入内存
  updateImmediate
}


/** 用户数据更新事件回调函数 */
type TUpdateRoleEventCallback = () => void;

type TUpdateRoleEventParam = Record<string, unknown>;

type TRoleEvent = {
  'ROLE_UPDATE_EVENT': TUpdateRoleEventParam,
};

type TFetchRoleInfo = {
  roleMap: TRoleMap,
  roleList: Role[],
};

/** key: roleId, value: Role */
export type TRoleMap = Record<string, Role>;

export type TRoleLabel = {
  roleId: string;
  text: string;
  color: string;
};

class RoleManager {
  /** 单实例 */
  private static instance: RoleManager;
  public static getInstance(): RoleManager {
    if (! RoleManager.instance) {
      RoleManager.instance = new RoleManager();
    }
    return RoleManager.instance;
  }

  /** 事件监听器 */
  protected eventListener: EventBus<TRoleEvent> = new EventBus();

  /** key: groupId + channelId, value: TRoleMap */
  private roleMapByGroupChannel: Record<string, TRoleMap> = {};

  /** key: groupId + channelId, value: Role[] */
  private roleListByGroupChannel: Record<string, Role[]> = {};

  /**
   * 从后台拉取群组角色信息
   * @param groupId
   * @param channelId
   */
  public async fetchRoleInfoFromRemote(
    groupId: string,
    channelId = COMMON_CHANNEL_ID,
  ): Promise<TFetchRoleInfo> {
    const key = getGroupChannelKey(groupId, channelId);
    const response = await roleApi.getGroupRoleList(groupId);
    const roleList = response.list;
    const roleListComputed = roleList.filter((item) => {
      const { id, setType, isShow, external } = item;
      const isUser = item.type === ROLE_TYPE.TY_USER;
      const isNormalRole = id === String(SPECIAL_ROLE_ID.ID_NORMAL);
      // 后台返回的角色名称会有重复，根据setType做去重处理；所有用户的isShow为false，因此依赖roleId判断显示
      const canShow = (setType === ROLE_SET_TYPE.ROLE_SET_GROUP && isShow && external) || isNormalRole;
      return isUser && canShow;
    });
    if (roleListComputed.findIndex(role => role.id === String(SPECIAL_ROLE_ID.ID_BOT_GROUP)) === -1) {
      roleListComputed.splice(1, 0, this.getBotGroupRole(groupId));
    }
    const roleMap: TRoleMap = {};
    roleListComputed.forEach((role) => {
      roleMap[role.id] = role;
    });
    this.roleMapByGroupChannel[key] = roleMap;
    this.roleListByGroupChannel[key] = roleListComputed;

    return {
      roleMap,
      roleList: roleListComputed,
    };
  }

  /**
   * 获取角色信息列表（通过群组频道）
   * @param groupId
   * @param channelId
   * @param updateType
   */
  public async getRoleListByGroupChannel(
    groupId: string,
    channelId = COMMON_CHANNEL_ID,
    updateType: RoleManagerUpdateType = RoleManagerUpdateType.updateNormal,
  ): Promise<Role[]> {
    const key = getGroupChannelKey(groupId, channelId);
    if (updateType === RoleManagerUpdateType.updateImmediate) {
      return (await this.fetchRoleInfoFromRemote(groupId)).roleList;
    }
    const roleList = this.roleListByGroupChannel[key];
    if (roleList) {
      return roleList;
    }
    return (await this.fetchRoleInfoFromRemote(groupId)).roleList;
  }

  /**
   * 获取角色信息Map（通过群组频道）
   * @param groupId
   * @param channelId
   * @param updateType
   */
  public async getRoleMapByGroupChannel(
    groupId: string,
    channelId = COMMON_CHANNEL_ID,
    updateType: RoleManagerUpdateType = RoleManagerUpdateType.updateNormal,
  ): Promise<TRoleMap> {
    const key = getGroupChannelKey(groupId, channelId);
    if (updateType === RoleManagerUpdateType.updateImmediate) {
      return (await this.fetchRoleInfoFromRemote(groupId)).roleMap;
    }
    const roleMap = this.roleMapByGroupChannel[key];
    if (roleMap) {
      return roleMap;
    }
    return (await this.fetchRoleInfoFromRemote(groupId)).roleMap;
  }

  /**
   * 获取角色标签信息
   */
  public async getRoleLabelList(param: {
    roleIdList: string[],
    groupId: string,
    channelId?: string,
    updateType?: RoleManagerUpdateType,
    showClose?: boolean,
  }): Promise<TRoleLabel[]> {
    const { roleIdList, groupId, channelId = COMMON_CHANNEL_ID } = param;
    const { updateType = RoleManagerUpdateType.updateNormal, showClose = true } = param;
    const roleMap = await this.getRoleMapByGroupChannel(groupId, channelId, updateType);
    if (!roleMap) {
      return [];
    }

    const roleLabelList: TRoleLabel[] = [];
    for (const roleId of roleIdList) {
      const role = roleMap[roleId];
      const { color = '', name = '' } = role || {};
      const { open = false, text = '' } = role?.display?.label || {};
      const canShow = open || showClose;
      const labelText = text || name;
      if (!canShow || !color || !labelText) {
        continue;
      }

      roleLabelList.push({
        roleId,
        color,
        text: labelText,
      });
    }
    return roleLabelList;
  }

  /**
   * 添加用户信息状态变化的监听器
   * @param callback 回调函数
   */
  protected addRoleUpdateEventListener(callback: TUpdateRoleEventCallback): void {
    this.eventListener.on('ROLE_UPDATE_EVENT', callback);
  }

  /**
   * 移除用户信息状态变化的监听器
   * @param callback 回调函数
   */
  protected removeUserUpdateEventListener(callback: TUpdateRoleEventCallback): void {
    this.eventListener.on('ROLE_UPDATE_EVENT', callback);
  }

  /**
   * 通知外部，用户信息状态有变化
   * @param groupId 群id
   * @param channelId 频道id(默认：'#', 非私密频道用户信息共用)
   */
  protected notifyUserUpdateEvent(
    groupId: string,
    channelId = COMMON_CHANNEL_ID,
  ) {
    this.eventListener.emit('ROLE_UPDATE_EVENT', {
      groupId,
      channelId,
    });
  }

  /**
   * 获取机器人群组的角色信息
   * - 前端根据UI显示需求，自行构造的角色信息
   * @param groupId 群id
   * @returns 角色信息
   */
  private getBotGroupRole(groupId: string) {
    return {
      id: String(SPECIAL_ROLE_ID.ID_BOT_GROUP),
      groupId,
      name: 'Bot',
      permsIds: [],
      weight: '-1',
      color: '',
      display: {
        label: {
          id: '0',
          text: '',
          open: false,
        },
      },
      external: false,
      isShow: false,
      permsSetIds: [],
      relateId: '',
      setId: '',
      setType: ROLE_SET_TYPE.ROLE_SET_NOT_USE,
      totalNum: '',
      tplId: '',
      type: ROLE_TYPE.TY_NOT_USE,
      expire: {
        open: false,
        duration: '',
        startTime: '',
      },
    };
  }
}

export { RoleManager };
